/* eslint-disable no-unused-vars */
import React from 'react';
import { FiCompass, FiCrosshair, FiMonitor, FiPenTool, FiAward, FiStar, FiShield, FiCpu } from 'react-icons/fi';

export const CAREER_TRACKS = {
  'Registered Land Surveyor': { color: '#b8860b', bg: '#fdf6e3', icon: FiAward },
  'Survey Technician': { color: '#4caf50', bg: '#e8f5e9', icon: FiCompass },
  'Instrument Operator': { color: '#2196f3', bg: '#e3f2fd', icon: FiCrosshair },
  'Office Technician': { color: '#9c27b0', bg: '#f3e5f5', icon: FiMonitor },
  'CAD Technician': { color: '#ff9800', bg: '#fff3e0', icon: FiPenTool },
  'Field Safety': { color: '#dc2626', bg: '#fee2e2', icon: FiShield },
  'Computer Literacy': { color: '#0891b2', bg: '#cffafe', icon: FiCpu },
};

// NGS career framework (effective 2026-05-07): tracks live in track groups,
// pay is by tier within group, three brackets per tier (Entry/Mid/Senior).
// Cross-group cert auto-qualifies for Senior bracket (Dual-Track Bracket Rule).
export const TRACK_GROUPS = {
  'Survey Technician':       'Field',
  'Instrument Operator':     'Field',
  'Office Technician':       'Office',
  'CAD Technician':          'Office',
  'Registered Land Surveyor': 'Licensed',
};

export const GROUP_STYLE = {
  Field:    { color: '#1565c0', bg: '#e3f2fd', label: 'Field Surveyor Group' },
  Office:   { color: '#7b1fa2', bg: '#f3e5f5', label: 'Office Surveyor Group' },
  Licensed: { color: '#b8860b', bg: '#fdf6e3', label: 'Licensed' },
};

export const GROUP_PAY_TIERS = {
  Field: [
    { tier: 1, entry: 18.00, mid: 19.00, senior: 20.00 },
    { tier: 2, entry: 20.00, mid: 21.00, senior: 22.00 },
    { tier: 3, entry: 22.00, mid: 24.00, senior: 26.00 },
    { tier: 4, entry: 26.00, mid: 29.00, senior: 32.00 },
    { tier: 5, entry: 32.00, mid: 35.00, senior: 38.00 },
    { tier: 6, entry: 38.00, mid: 40.00, senior: 42.00 }, // Tier 6 Senior is "$42+"
  ],
  Office: [
    { tier: 1, entry: 18.00, mid: 19.00, senior: 20.00 },
    { tier: 2, entry: 20.00, mid: 21.50, senior: 23.00 },
    { tier: 3, entry: 23.00, mid: 25.50, senior: 28.00 },
    { tier: 4, entry: 28.00, mid: 31.00, senior: 34.00 },
    { tier: 5, entry: 34.00, mid: 36.00, senior: 38.00 },
    { tier: 6, entry: 38.00, mid: 40.00, senior: 42.00 }, // Tier 6 Senior is "$42+"
  ],
};

// Time-in-tier minimums between successive promotions (Tier 1→2, 2→3, ... 5→6)
export const TIER_PROMOTION_MINIMUMS = ['6 months', '2 years', '6 months', '2 years', '2 years'];

// Returns the unique non-Licensed groups a user is in based on their tracks.
export function getUserGroups(tracks) {
  const groups = new Set();
  for (const t of tracks || []) {
    const g = TRACK_GROUPS[t.career_track];
    if (g && g !== 'Licensed') groups.add(g);
  }
  return [...groups];
}

// True if user holds at least one track in BOTH Field and Office groups.
// Triggers the Dual-Track Bracket Rule (auto-Senior bracket at current tier).
export function isDualTrack(tracks) {
  const groups = getUserGroups(tracks);
  return groups.includes('Field') && groups.includes('Office');
}

// Format a tier+bracket as a hourly-rate string.
export function formatTierBracket(group, tier, bracket) {
  const tiers = GROUP_PAY_TIERS[group];
  if (!tiers) return null;
  const row = tiers.find(r => r.tier === tier);
  if (!row) return null;
  const key = (bracket || '').toLowerCase();
  const val = row[key];
  if (val == null) return null;
  const suffix = (tier === 6 && key === 'senior') ? '+' : '';
  return `$${val.toFixed(2)}${suffix}/hr`;
}

function LevelStars({ level, size, color, filledColor }) {
  return (
    <div style={{ display: 'flex', gap: size > 10 ? '2px' : '1px', justifyContent: 'center' }}>
      {[1, 2, 3].map(i => (
        <FiStar
          key={i}
          size={size}
          style={{
            fill: i <= level ? filledColor : 'none',
            color: i <= level ? filledColor : color + '55',
            strokeWidth: i <= level ? 2.5 : 1.5
          }}
        />
      ))}
    </div>
  );
}

export function CareerBadge({ track, level, size = 'normal' }) {
  const config = CAREER_TRACKS[track];
  if (!config) return null;

  const Icon = config.icon;
  const isSmall = size === 'small';
  const isLarge = size === 'large';

  // Large: emblem badge for Profile page
  if (isLarge) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        width: '110px'
      }}>
        {/* Shield shape */}
        <div style={{
          width: '72px',
          height: '80px',
          background: `linear-gradient(160deg, ${config.color}, ${config.color}cc)`,
          borderRadius: '12px 12px 36px 36px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          boxShadow: `0 4px 14px ${config.color}44`,
          border: `2px solid ${config.color}`,
          position: 'relative'
        }}>
          <Icon size={28} color="white" strokeWidth={2} />
          <LevelStars level={level} size={13} color="white" filledColor="#FFD700" />
        </div>
        {/* Track name */}
        <div style={{
          fontSize: '11px',
          fontWeight: '700',
          color: config.color,
          textAlign: 'center',
          lineHeight: '1.2',
          maxWidth: '110px'
        }}>
          {track}
        </div>
      </div>
    );
  }

  // Normal: compact badge with icon + stars
  if (!isSmall) {
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: config.bg,
        padding: '6px 12px',
        borderRadius: '12px',
        border: `1.5px solid ${config.color}40`,
        whiteSpace: 'nowrap'
      }}>
        <div style={{
          width: '30px',
          height: '30px',
          borderRadius: '8px',
          backgroundColor: config.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon size={16} color="white" strokeWidth={2.5} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: config.color, lineHeight: '1.1' }}>
            {track}
          </span>
          <LevelStars level={level} size={10} color={config.color} filledColor={config.color} />
        </div>
      </div>
    );
  }

  // Small: icon + stars only (for sidebar/compact)
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      backgroundColor: config.bg,
      padding: '3px 8px',
      borderRadius: '8px',
      border: `1px solid ${config.color}30`
    }}>
      <div style={{
        width: '20px',
        height: '20px',
        borderRadius: '5px',
        backgroundColor: config.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon size={11} color="white" strokeWidth={2.5} />
      </div>
      <LevelStars level={level} size={8} color={config.color} filledColor={config.color} />
    </div>
  );
}

export function CareerBadgeList({ tracks, size = 'normal' }) {
  if (!tracks || tracks.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: size === 'large' ? '16px' : '6px',
      justifyContent: size === 'large' ? 'center' : 'flex-start'
    }}>
      {tracks.map(t => (
        <CareerBadge key={t.id || t.career_track} track={t.career_track} level={t.career_level} size={size} />
      ))}
    </div>
  );
}

// Pill showing a track group (Field / Office). Used to surface group membership.
export function GroupBadge({ group }) {
  const style = GROUP_STYLE[group];
  if (!style) return null;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: 0.3,
      color: style.color,
      backgroundColor: style.bg,
      border: `1px solid ${style.color}40`
    }}>
      {style.label}
    </span>
  );
}

// Pill shown when user holds at least one cert in BOTH Field and Office groups.
// Indicates eligibility for the Dual-Track Bracket Rule (auto-Senior bracket).
export function DualTrackBadge() {
  return (
    <span title="Holds certifications in both Field and Office groups — qualifies for Senior bracket at current tier."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 10px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: 0.3,
        color: '#0d6b3b',
        backgroundColor: '#dcfce7',
        border: '1px solid #16a34a40'
      }}>
      <FiStar size={11} style={{ fill: '#16a34a', color: '#16a34a' }} /> Dual-Track Bracket
    </span>
  );
}

// Reference table showing the pay tiers + brackets for a single group.
// Renders inline; safe to drop into a card or modal.
export function PayTierTable({ group, highlightTier, highlightBracket }) {
  const tiers = GROUP_PAY_TIERS[group];
  if (!tiers) return null;
  const style = GROUP_STYLE[group];
  const cellPad = '6px 10px';
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: style.color, marginBottom: 4 }}>
        {style.label} — pay tiers
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 380 }}>
        <thead>
          <tr style={{ backgroundColor: style.bg }}>
            <th style={{ padding: cellPad, textAlign: 'left', border: '1px solid #e2e8f0' }}>Tier</th>
            <th style={{ padding: cellPad, textAlign: 'right', border: '1px solid #e2e8f0' }}>Entry</th>
            <th style={{ padding: cellPad, textAlign: 'right', border: '1px solid #e2e8f0' }}>Mid</th>
            <th style={{ padding: cellPad, textAlign: 'right', border: '1px solid #e2e8f0' }}>Senior</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map(row => {
            const isHL = highlightTier === row.tier;
            const cellStyle = (bracket) => ({
              padding: cellPad,
              textAlign: 'right',
              border: '1px solid #e2e8f0',
              fontWeight: isHL && highlightBracket && highlightBracket.toLowerCase() === bracket ? 700 : 400,
              backgroundColor: isHL && highlightBracket && highlightBracket.toLowerCase() === bracket ? style.bg : 'transparent'
            });
            const fmt = (v, isLastSenior) => `$${v.toFixed(2)}${isLastSenior ? '+' : ''}`;
            return (
              <tr key={row.tier}>
                <td style={{ padding: cellPad, border: '1px solid #e2e8f0', fontWeight: isHL ? 700 : 500 }}>
                  Tier {row.tier}
                </td>
                <td style={cellStyle('entry')}>{fmt(row.entry, false)}</td>
                <td style={cellStyle('mid')}>{fmt(row.mid, false)}</td>
                <td style={cellStyle('senior')}>{fmt(row.senior, row.tier === 6)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
        Time-in-tier minimums: 6mo &rarr; 2yr &rarr; 6mo &rarr; 2yr &rarr; 2yr.
      </div>
    </div>
  );
}

export default CareerBadge;
